/** Officer-facing value formatting — one shared pipeline for the engine
 * debris the 2026-07-24 audits caught in officer prose (officer-POV finding
 * 5c / value-audit anti-wow 4):
 *
 *   - Python reprs leaking verbatim: `Decimal('25000000.000000')`,
 *     `datetime.date(2026, 3, 5)` (persisted gate-failure details render in
 *     the chase + evidence sign-off panes),
 *   - unformatted decimals in drawer facts ("10000.000000"),
 *   - raw numeric readings in the trade-duplicate diff ("8000.000000").
 *
 * Display-only: these helpers NEVER feed values back into edits or
 * comparisons — diffs keep comparing the raw readings.
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const NUM_RE = /^-?\d+(?:\.\d+)?$/

/** Officer figure rule (Ale 2026-09-02, effective immediately): NO decimals
 *  unless the figure actually has them; when it does, EXACTLY two, rounded
 *  half-up. Thousands grouped. Non-numeric strings pass through untouched.
 *
 *    "25000000.000000" → "25,000,000"   "400.000000" → "400"
 *    "8000.5"          → "8,000.50"     "12.345"     → "12.35"
 *    "-25000000.000000"→ "-25,000,000"  "0.005"      → "0.01"
 *
 *  Pure string arithmetic on the integer part so 15-digit ledgers never go
 *  through a float; only the fraction is rounded. */
export function formatNumberString(s: string): string {
  if (!NUM_RE.test(s)) return s
  const neg = s.startsWith('-')
  const [intRaw, fracPart = ''] = (neg ? s.slice(1) : s).split('.')
  let intPart = intRaw
  let frac = ''
  if (fracPart.replace(/0+$/, '')) {
    // Round the fraction to 2 places, half-up, carrying into the integer.
    const scaled = Math.round(Number(`0.${fracPart}`) * 100)
    if (scaled >= 100) {
      intPart = (BigInt(intPart) + 1n).toString()
      frac = ''
    } else {
      frac = scaled === 0 ? '' : String(scaled).padStart(2, '0')
    }
  }
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return `${neg ? '-' : ''}${grouped}${frac ? `.${frac}` : ''}`
}

/** A backend token — lowercase words joined by underscores — read as words:
 *  "collective_vehicle" → "collective vehicle". Anything that is not shaped
 *  like a machine token (names, ISINs, registry codes, dates) is untouched.
 *  (Ale 2026-09-02: no backend variable name ever reaches the UI with "_".) */
const TOKEN_RE = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+$/
export function humanizeToken(s: string): string {
  return TOKEN_RE.test(s) ? s.replace(/_/g, ' ') : s
}

/** Every machine token inside a sentence, e.g. an engine reason
 *  "no_confirmed_trade_date — record says …" → "no confirmed trade date — …". */
export function humanizeTokensIn(text: string): string {
  return text.replace(/\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+\b/g, (t) => t.replace(/_/g, ' '))
}

/** "5 Mar 2026" from numeric year/month/day (repr replacement). */
function formatDateParts(y: string, m: string, d: string): string {
  const month = MONTHS[Number(m) - 1] ?? m
  return `${Number(d)} ${month} ${y}`
}

/** K7 (2026-08-31): ONE officer date style everywhere — "30 Jun 2026",
 *  exactly as the locked mocks print it. Accepts ISO date / datetime
 *  strings; anything unparseable passes through untouched (never worse
 *  than the raw value). Display-only, like everything in this module. */
export function formatOfficerDate(v: string | null | undefined): string | null {
  if (!v) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v)
  if (!m) return v
  return formatDateParts(m[1], m[2], m[3])
}

/** "2 Sep 2026 14:12" — the officer date followed by the local wall-clock
 *  time, for a signature line ("signed … · 2 Sep 2026 14:12"). A bare date
 *  (no time part) prints as the date alone; anything unparseable passes
 *  through like formatOfficerDate. */
export function formatOfficerDateTime(v: string | null | undefined): string | null {
  if (!v) return null
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return formatOfficerDate(v)
  const d = new Date(v)
  if (Number.isNaN(d.getTime())) return formatOfficerDate(v)
  const two = (n: number) => String(n).padStart(2, '0')
  const date = formatDateParts(String(d.getFullYear()), two(d.getMonth() + 1), two(d.getDate()))
  return `${date} ${two(d.getHours())}:${two(d.getMinutes())}`
}

/** Replace Python reprs inside a sentence with officer-readable values:
 *  `Decimal('25000000.000000')` → "25,000,000",
 *  `datetime.date(2026, 3, 5)` → "5 Mar 2026". Everything else untouched. */
export function cleanEngineText(text: string): string {
  return text
    .replace(/Decimal\('(-?\d+(?:\.\d+)?)'\)/g, (_, n: string) => formatNumberString(n))
    .replace(/datetime\.date\((\d{4}),\s*(\d{1,2}),\s*(\d{1,2})\)/g, (_, y, m, d) =>
      formatDateParts(y, m, d)
    )
}

/** Drawer-fact / diff-cell value: bare numbers get grouped and trimmed;
 *  "25000000.000000 EUR"-style readings keep their unit suffix; anything
 *  else (names, dates, enums already humanized) passes through. */
export function formatOfficerValue(v: unknown): string {
  const s = String(v)
  if (NUM_RE.test(s)) return formatNumberString(s)
  const withUnit = /^(-?\d+(?:\.\d+)?)\s+(.{1,12})$/.exec(s)
  if (withUnit && NUM_RE.test(withUnit[1])) {
    return `${formatNumberString(withUnit[1])} ${withUnit[2]}`
  }
  return humanizeTokensIn(cleanEngineText(s))
}
